
import React from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import PartnerForm from './components/PartnerForm';
import { useSEO, getOrganizationSchema, getLocalBusinessSchema } from '../../utils/seo';
import { useLocation } from 'react-router-dom';

const HomePage: React.FC = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');

  // SEO Implementation
  useSEO({
    title: isNL ? 'Thyamis - Professionele IT-oplossingen, Hardware, Software & AI-diensten' : 'Thyamis - Professional IT Solutions, Hardware, Software & AI Services',
    description: isNL ? 'Thyamis is uw vertrouwde partner voor uitgebreide IT-oplossingen sinds 2025. Wij bieden enterprise hardware, software, cloud diensten, cyberbeveiliging en geavanceerde AI-oplossingen om uw digitale transformatie en bedrijfssucces te stimuleren.' : 'Thyamis is your trusted partner for comprehensive IT solutions since 2025. We provide enterprise hardware, software, cloud services, cybersecurity, and cutting-edge AI solutions to drive your digital transformation and business success.',
    keywords: isNL ? 'IT-oplossingen, enterprise technologie, hardware, software, cloud diensten, cyberbeveiliging, AI-oplossingen, digitale transformatie, beheerde IT-diensten' : 'IT solutions, enterprise technology, hardware, software, cloud services, cybersecurity, AI solutions, digital transformation, managed IT services',
    canonical: isNL ? '/nl' : '/',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        getLocalBusinessSchema(),
        {
          '@type': 'WebSite',
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          name: 'Thyamis',
          description: isNL ? 'Professionele IT-oplossingen, Hardware, Software & AI-diensten' : 'Professional IT Solutions, Hardware, Software & AI Services',
          publisher: {
            '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
          },
        },
      ],
    },
  });

  const t = {
    hero: {
      title: isNL ? 'Uw One-Stop Shop voor Enterprise IT-oplossingen' : 'Your One-Stop Shop for Enterprise IT Solutions',
      subtitle: isNL ? 'Thyamis levert uitgebreide hardware-, software- en AI-oplossingen om uw bedrijf te laten floreren in het huidige technologiegedreven landschap.' : 'Thyamis delivers comprehensive hardware, software, and AI solutions to help your business thrive in today\'s technology-driven landscape.',
      cta: isNL ? 'Partner met Thyamis' : 'Partner with Thyamis'
    },
    solutions: {
      title: isNL ? 'Volledige IT-oplossingen voor Uw Bedrijf' : 'Complete IT Solutions for Your Business',
      subtitle: isNL ? 'Van hardware tot AI bieden wij alles wat uw bedrijf nodig heeft om te slagen in het digitale tijdperk' : 'From hardware to AI, we provide everything your business needs to succeed in the digital age',
      hardware: {
        title: isNL ? 'Hardware Oplossingen' : 'Hardware Solutions',
        desc: isNL ? 'Enterprise-grade servers, netwerkapparatuur en computeroplossingen van toonaangevende fabrikanten.' : 'Enterprise-grade servers, networking equipment, and computing solutions from leading manufacturers.'
      },
      software: {
        title: isNL ? 'Software Licenties' : 'Software Licensing',
        desc: isNL ? 'Zakelijke software, productiviteitssuites, beveiligingsoplossingen en gespecialiseerde applicaties.' : 'Business software, productivity suites, security solutions, and specialized applications.'
      },
      itServices: {
        title: isNL ? 'IT-diensten' : 'IT Services',
        desc: isNL ? 'Beheerde diensten, technische ondersteuning, implementatie en strategisch IT-advies.' : 'Managed services, technical support, implementation, and strategic IT consulting.'
      },
      ai: {
        title: isNL ? 'Generatieve AI' : 'Generative AI',
        desc: isNL ? 'Enterprise AI-oplossingen, maatwerkontwikkeling en integratiediensten voor bedrijven.' : 'Enterprise AI solutions, custom development, and integration services for business.'
      }
    },
    products: {
      title: isNL ? 'Uitgelichte Producten' : 'Featured Products',
      filters: {
        all: isNL ? 'Alles' : 'All',
        hardware: isNL ? 'Hardware' : 'Hardware',
        software: isNL ? 'Software' : 'Software',
        services: isNL ? 'Diensten' : 'Services'
      },
      categories: {
        hardware: isNL ? 'Hardware' : 'Hardware',
        software: isNL ? 'Software' : 'Software',
        aiServices: isNL ? 'AI-diensten' : 'AI Services'
      },
      items: [
        {
          name: isNL ? 'Dell PowerEdge R740 Server' : 'Dell PowerEdge R740 Server',
          desc: isNL ? 'Enterprise rack server met dubbele Intel Xeon processors en redundante voeding.' : 'Enterprise rack server with dual Intel Xeon processors and redundant power.'
        },
        {
          name: isNL ? 'Microsoft 365 Business Premium' : 'Microsoft 365 Business Premium',
          desc: isNL ? 'Complete productiviteitssuite met geavanceerde beveiligingsfuncties (25 gebruikers).' : 'Complete productivity suite with advanced security features (25 users).'
        },
        {
          name: isNL ? 'Cisco Firepower 1120 NGFW' : 'Cisco Firepower 1120 NGFW',
          desc: isNL ? 'Next-generation firewall met geavanceerde bedreigingsbescherming voor MKB.' : 'Next-generation firewall with advanced threat protection for SMBs.'
        },
        {
          name: isNL ? 'Enterprise AI Implementatie' : 'Enterprise AI Implementation',
          desc: isNL ? 'Maatwerk AI-oplossingen ontwikkeling en integratie voor enterprise workflows.' : 'Custom AI solution development and integration for enterprise workflows.'
        }
      ],
      cta: isNL ? 'Offerte Aanvragen' : 'Request Quote'
    },
    services: {
      title: isNL ? 'Uitgebreide IT-diensten' : 'Comprehensive IT Services',
      subtitle: isNL ? 'Van strategische planning tot implementatie en doorlopende ondersteuning leveren wij end-to-end IT-diensten op maat van uw bedrijfsbehoeften.' : 'From strategic planning to implementation and ongoing support, we deliver end-to-end IT services tailored to your business needs.',
      cloud: {
        title: isNL ? 'Cloud Diensten' : 'Cloud Services',
        desc: isNL ? 'Migreer, beheer en optimaliseer uw cloud-infrastructuur met onze deskundige cloud diensten. Wij behandelen AWS, Azure en Google Cloud implementaties.' : 'Migrate, manage, and optimize your cloud infrastructure with our expert cloud services. We handle AWS, Azure, and Google Cloud deployments.',
        features: [
          isNL ? 'Cloud migratie & strategie' : 'Cloud migration & strategy',
          isNL ? 'Hybride cloud oplossingen' : 'Hybrid cloud solutions',
          isNL ? 'Cloud beveiliging & compliance' : 'Cloud security & compliance'
        ]
      },
      security: {
        title: isNL ? 'Cyberbeveiliging' : 'Cybersecurity',
        desc: isNL ? 'Bescherm uw bedrijf met uitgebreide beveiligingsoplossingen die uw gegevens, systemen en netwerken beschermen tegen evoluerende bedreigingen.' : 'Protect your business with comprehensive security solutions that safeguard your data, systems, and networks from evolving threats.',
        features: [
          isNL ? 'Beveiligingsbeoordelingen' : 'Security assessments',
          isNL ? 'Endpoint bescherming' : 'Endpoint protection',
          isNL ? '24/7 beveiligingsmonitoring' : '24/7 security monitoring'
        ]
      },
      ai: {
        title: isNL ? 'AI-integratie' : 'AI Integration',
        desc: isNL ? 'Benut de kracht van kunstmatige intelligentie om uw bedrijfsprocessen te transformeren, efficiëntie te verbeteren en innovatie te stimuleren.' : 'Leverage the power of artificial intelligence to transform your business processes, improve efficiency, and drive innovation.',
        features: [
          isNL ? 'AI-gereedheidsanalyse' : 'AI readiness assessment',
          isNL ? 'Maatwerk AI-ontwikkeling' : 'Custom AI development',
          isNL ? 'AI workflow automatisering' : 'AI workflow automation'
        ]
      }
    },
    aiSection: {
      label: isNL ? 'GENERATIEVE AI-OPLOSSINGEN' : 'GENERATIVE AI SOLUTIONS',
      title: isNL ? 'Transformeer Uw Bedrijf met Enterprise AI' : 'Transform Your Business with Enterprise AI',
      desc: isNL ? 'Thyamis biedt geavanceerde generatieve AI-oplossingen die specifiek zijn ontworpen voor enterprise behoeften. Onze AI-diensten helpen u processen te automatiseren, inzichten uit uw gegevens te halen en nieuwe mogelijkheden voor innovatie te creëren.' : 'Thyamis provides cutting-edge generative AI solutions designed specifically for enterprise needs. Our AI services help you automate processes, gain insights from your data, and create new opportunities for innovation.',
      features: [
        {
          title: isNL ? 'Maatwerk AI-ontwikkeling' : 'Custom AI Development',
          desc: isNL ? 'Op maat gemaakte AI-oplossingen speciaal gebouwd voor uw bedrijfsbehoeften en workflows.' : 'Tailored AI solutions built specifically for your business needs and workflows.'
        },
        {
          title: isNL ? 'Data-analyse & Inzichten' : 'Data Analysis & Insights',
          desc: isNL ? 'Haal waardevolle inzichten uit uw gegevens met behulp van geavanceerde AI-analyse.' : 'Extract valuable insights from your data using advanced AI analytics.'
        },
        {
          title: isNL ? 'AI-integratiediensten' : 'AI Integration Services',
          desc: isNL ? 'Integreer AI-mogelijkheden naadloos in uw bestaande systemen en workflows.' : 'Seamlessly integrate AI capabilities into your existing systems and workflows.'
        }
      ],
      cta: isNL ? 'Ontdek AI-oplossingen' : 'Explore AI Solutions'
    },
    infrastructure: {
      label: isNL ? 'ENTERPRISE INFRASTRUCTUUR' : 'ENTERPRISE INFRASTRUCTURE',
      title: isNL ? 'Schaalbare IT-infrastructuur voor Groeiende Bedrijven' : 'Scalable IT Infrastructure for Growing Businesses',
      desc: isNL ? 'Bouw een robuuste basis voor uw bedrijf met onze enterprise-grade infrastructuuroplossingen. Van datacenters tot cloud implementaties bieden wij de ruggengraat die uw organisatie nodig heeft om efficiënt te schalen.' : 'Build a robust foundation for your business with our enterprise-grade infrastructure solutions. From data centers to cloud deployments, we provide the backbone your organization needs to scale efficiently.',
      features: [
        {
          title: isNL ? 'Datacenter Oplossingen' : 'Data Center Solutions',
          desc: isNL ? 'Compleet datacenter ontwerp, implementatie en beheer diensten.' : 'Complete data center design, implementation, and management services.'
        },
        {
          title: isNL ? 'Hybride Cloud Architectuur' : 'Hybrid Cloud Architecture',
          desc: isNL ? 'Integreer on-premises en cloud resources naadloos voor optimale prestaties.' : 'Seamlessly integrate on-premises and cloud resources for optimal performance.'
        },
        {
          title: isNL ? 'Beveiliging & Compliance' : 'Security & Compliance',
          desc: isNL ? 'Enterprise-grade beveiligingsmaatregelen en compliance frameworks.' : 'Enterprise-grade security measures and compliance frameworks.'
        }
      ]
    },
    digital: {
      label: isNL ? 'DIGITALE TRANSFORMATIE' : 'DIGITAL TRANSFORMATION',
      title: isNL ? 'Moderniseer Uw Bedrijfsactiviteiten' : 'Modernize Your Business Operations',
      desc: isNL ? 'Transformeer uw organisatie met geavanceerde digitale oplossingen die processen stroomlijnen, productiviteit verbeteren en innovatie stimuleren in alle afdelingen.' : 'Transform your organization with cutting-edge digital solutions that streamline processes, enhance productivity, and drive innovation across all departments.',
      features: [
        {
          title: isNL ? 'Mobile-First Oplossingen' : 'Mobile-First Solutions',
          desc: isNL ? 'Stel uw personeel in staat met mobiele applicaties en responsieve platforms.' : 'Enable your workforce with mobile applications and responsive platforms.'
        },
        {
          title: isNL ? 'Business Intelligence' : 'Business Intelligence',
          desc: isNL ? 'Neem datagestuurde beslissingen met geavanceerde analyse- en rapportagetools.' : 'Make data-driven decisions with advanced analytics and reporting tools.'
        },
        {
          title: isNL ? 'Procesautomatisering' : 'Process Automation',
          desc: isNL ? 'Automatiseer repetitieve taken en optimaliseer workflows voor maximale efficiëntie.' : 'Automate repetitive tasks and optimize workflows for maximum efficiency.'
        }
      ],
      cta: isNL ? 'Start Uw Transformatie' : 'Start Your Transformation'
    },
    cta: {
      title: isNL ? 'Klaar om Uw IT-infrastructuur te Transformeren?' : 'Ready to Transform Your IT Infrastructure?',
      desc: isNL ? 'Neem vandaag nog contact op met ons team van experts om te bespreken hoe Thyamis uw bedrijf kan helpen de nieuwste technologieoplossingen te benutten voor groei en innovatie.' : 'Contact our team of experts today to discuss how Thyamis can help your business leverage the latest technology solutions for growth and innovation.',
      primary: isNL ? 'Aan de Slag' : 'Get Started',
      secondary: isNL ? 'Bekijk Oplossingen' : 'View Solutions'
    },
    common: {
      learnMore: isNL ? 'Meer Informatie' : 'Learn More'
    }
  };

  return (
    <div className="bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-start pt-20" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/9ca2a2a054124f82de7876fc7a60a3df.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="container mx-auto px-4 py-8 relative">
          <div className="w-full max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight overflow-hidden">
              <style>{`
                @keyframes slideInRight {
                  0% { 
                    transform: translateX(-100%);
                    opacity: 0;
                  }
                  100% { 
                    transform: translateX(0);
                    opacity: 1;
                  }
                }
                @keyframes colorPulse {
                  0%, 100% { color: white; }
                  50% { color: #06b6d4; }
                }
                .word-slide {
                  display: inline-block;
                  animation: slideInRight 0.6s ease-out forwards, colorPulse 3s ease-in-out infinite;
                }
                .word-slide-1 { animation-delay: 0s, 0.6s; }
                .word-slide-2 { animation-delay: 0.1s, 0.7s; }
                .word-slide-3 { animation-delay: 0.2s, 0.8s; }
                .word-slide-4 { animation-delay: 0.3s, 0.9s; }
                .word-slide-5 { animation-delay: 0.4s, 1s; }
                .word-slide-6 { animation-delay: 0.5s, 1.1s; }
                .word-slide-7 { animation-delay: 0.6s, 1.2s; }
                .word-slide-8 { animation-delay: 0.7s, 1.3s; }
              `}</style>
              {isNL ? (
                <>
                  <span className="word-slide word-slide-1">Uw</span>{' '}
                  <span className="word-slide word-slide-2">One-Stop</span>{' '}
                  <span className="word-slide word-slide-3">Shop</span>{' '}
                  <span className="word-slide word-slide-4">voor</span>{' '}
                  <span className="word-slide word-slide-5">Enterprise</span>{' '}
                  <span className="word-slide word-slide-6">IT-oplossingen</span>
                </>
              ) : (
                <>
                  <span className="word-slide word-slide-1">Your</span>{' '}
                  <span className="word-slide word-slide-2">One-Stop</span>{' '}
                  <span className="word-slide word-slide-3">Shop</span>{' '}
                  <span className="word-slide word-slide-4">for</span>{' '}
                  <span className="word-slide word-slide-5">Enterprise</span>{' '}
                  <span className="word-slide word-slide-6">IT</span>{' '}
                  <span className="word-slide word-slide-7">Solutions</span>
                </>
              )}
            </h1>
            <p className="text-lg text-white mb-8">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#partner-form"
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Complete IT Solutions Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {t.solutions.title}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {t.solutions.subtitle}
            </p>
          </div>
          
          <style>{`
            .solution-flip-card {
              perspective: 1000px;
            }
            .solution-flip-card-inner {
              transition: transform 0.6s;
              transform-style: preserve-3d;
            }
            @media (min-width: 1024px) {
              .solution-flip-card:hover .solution-flip-card-inner {
                transform: rotateY(180deg);
              }
            }
            .solution-flip-card-front,
            .solution-flip-card-back {
              backface-visibility: hidden;
              -webkit-backface-visibility: hidden;
            }
            .solution-flip-card-back {
              transform: rotateY(180deg);
            }
          `}</style>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Hardware Solutions */}
            <div className="solution-flip-card cursor-pointer">
              <div className="solution-flip-card-inner relative h-[320px]">
                {/* Front */}
                <div className="solution-flip-card-front absolute inset-0 rounded-lg overflow-hidden shadow-md">
                  <div className="h-[240px] overflow-hidden">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/874e8baa9df05dc3d9a03f595e7eb261.jpg"
                      alt={t.solutions.hardware.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-primary p-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">{t.solutions.hardware.title}</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="solution-flip-card-back absolute inset-0 bg-white rounded-lg shadow-md border-2 border-primary flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
                    <div className="w-8 h-8 flex items-center justify-center text-white">
                      <i className="ri-server-line ri-2x"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t.solutions.hardware.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {t.solutions.hardware.desc}
                  </p>
                  <a href={`${isNL ? '/nl' : ''}/products/servers-storage`} className="text-primary font-medium flex items-center hover:underline">
                    <span>{t.common.learnMore}</span>
                    <div className="w-4 h-4 flex items-center justify-center ml-1">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                </div>
              </div>
              {/* Mobile Modal Trigger */}
              <div className="lg:hidden mt-4 bg-white rounded-lg shadow-md p-4 text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3">
                  <div className="w-6 h-6 flex items-center justify-center text-white">
                    <i className="ri-server-line"></i>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {t.solutions.hardware.desc}
                </p>
                <a href={`${isNL ? '/nl' : ''}/products/servers-storage`} className="text-primary font-medium text-sm hover:underline">{t.common.learnMore} →</a>
              </div>
            </div>

            {/* Software Licensing */}
            <div className="solution-flip-card cursor-pointer">
              <div className="solution-flip-card-inner relative h-[320px]">
                {/* Front */}
                <div className="solution-flip-card-front absolute inset-0 rounded-lg overflow-hidden shadow-md">
                  <div className="h-[240px] overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Software%20licensing%20and%20business%20applications%20with%20modern%20interface%20design%20on%20clean%20background&width=400&height=300&seq=softwarelic&orientation=landscape"
                      alt={t.solutions.software.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-green-600 p-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">{t.solutions.software.title}</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="solution-flip-card-back absolute inset-0 bg-white rounded-lg shadow-md border-2 border-green-600 flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
                    <div className="w-8 h-8 flex items-center justify-center text-white">
                      <i className="ri-apps-line ri-2x"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t.solutions.software.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {t.solutions.software.desc}
                  </p>
                  <a href={`${isNL ? '/nl' : ''}/microsoft-365`} className="text-green-600 font-medium flex items-center hover:underline">
                    <span>{t.common.learnMore}</span>
                    <div className="w-4 h-4 flex items-center justify-center ml-1">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                </div>
              </div>
              {/* Mobile Content */}
              <div className="lg:hidden mt-4 bg-white rounded-lg shadow-md p-4 text-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <div className="w-6 h-6 flex items-center justify-center text-white">
                    <i className="ri-apps-line"></i>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {t.solutions.software.desc}
                </p>
                <a href={`${isNL ? '/nl' : ''}/microsoft-365`} className="text-green-600 font-medium text-sm hover:underline">{t.common.learnMore} →</a>
              </div>
            </div>

            {/* IT Services */}
            <div className="solution-flip-card cursor-pointer">
              <div className="solution-flip-card-inner relative h-[320px]">
                {/* Front */}
                <div className="solution-flip-card-front absolute inset-0 rounded-lg overflow-hidden shadow-md">
                  <div className="h-[240px] overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=IT%20services%20and%20technical%20support%20with%20professional%20team%20working%20on%20technology%20solutions%20on%20clean%20background&width=400&height=300&seq=itservices&orientation=landscape"
                      alt={t.solutions.itServices.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-teal-600 p-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">{t.solutions.itServices.title}</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="solution-flip-card-back absolute inset-0 bg-white rounded-lg shadow-md border-2 border-teal-600 flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mb-4">
                    <div className="w-8 h-8 flex items-center justify-center text-white">
                      <i className="ri-customer-service-2-line ri-2x"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t.solutions.itServices.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {t.solutions.itServices.desc}
                  </p>
                  <a href={`${isNL ? '/nl' : ''}/managed-it-services`} className="text-teal-600 font-medium flex items-center hover:underline">
                    <span>{t.common.learnMore}</span>
                    <div className="w-4 h-4 flex items-center justify-center ml-1">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                </div>
              </div>
              {/* Mobile Content */}
              <div className="lg:hidden mt-4 bg-white rounded-lg shadow-md p-4 text-center">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <div className="w-6 h-6 flex items-center justify-center text-white">
                    <i className="ri-customer-service-2-line"></i>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {t.solutions.itServices.desc}
                </p>
                <a href={`${isNL ? '/nl' : ''}/managed-it-services`} className="text-teal-600 font-medium text-sm hover:underline">{t.common.learnMore} →</a>
              </div>
            </div>

            {/* Generative AI */}
            <div className="solution-flip-card cursor-pointer">
              <div className="solution-flip-card-inner relative h-[320px]">
                {/* Front */}
                <div className="solution-flip-card-front absolute inset-0 rounded-lg overflow-hidden shadow-md">
                  <div className="h-[240px] overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Generative%20AI%20and%20artificial%20intelligence%20technology%20with%20neural%20networks%20and%20machine%20learning%20visualization%20on%20clean%20background&width=400&height=300&seq=genai&orientation=landscape"
                      alt={t.solutions.ai.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="bg-amber-600 p-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">{t.solutions.ai.title}</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="solution-flip-card-back absolute inset-0 bg-white rounded-lg shadow-md border-2 border-amber-600 flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-4">
                    <div className="w-8 h-8 flex items-center justify-center text-white">
                      <i className="ri-ai-generate ri-2x"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t.solutions.ai.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {t.solutions.ai.desc}
                  </p>
                  <a href={`${isNL ? '/nl' : ''}/ai-consulting`} className="text-amber-600 font-medium flex items-center hover:underline">
                    <span>{t.common.learnMore}</span>
                    <div className="w-4 h-4 flex items-center justify-center ml-1">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                </div>
              </div>
              {/* Mobile Content */}
              <div className="lg:hidden mt-4 bg-white rounded-lg shadow-md p-4 text-center">
                <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <div className="w-6 h-6 flex items-center justify-center text-white">
                    <i className="ri-ai-generate"></i>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {t.solutions.ai.desc}
                </p>
                <a href={`${isNL ? '/nl' : ''}/ai-consulting`} className="text-amber-600 font-medium text-sm hover:underline">{t.common.learnMore} →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-3xl font-bold">{t.products.title}</h2>
            <div className="flex space-x-2">
              <button className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap font-medium text-sm cursor-pointer">
                {t.products.filters.all}
              </button>
              <button className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg whitespace-nowrap font-medium text-sm cursor-pointer hover:bg-gray-200">
                {t.products.filters.hardware}
              </button>
              <button className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg whitespace-nowrap font-medium text-sm cursor-pointer hover:bg-gray-200">
                {t.products.filters.software}
              </button>
              <button className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg whitespace-nowrap font-medium text-sm cursor-pointer hover:bg-gray-200">
                {t.products.filters.services}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {/* Product 1 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Dell%20PowerEdge%20R740%20enterprise%20rack%20server%20on%20clean%20white%20background%2C%20professional%20server%20hardware%20with%20blue%20LED%20indicators&width=400&height=400&seq=dellserver&orientation=squarish"
                  alt={t.products.items[0].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded-full">{t.products.categories.hardware}</span>
                  </div>
                  <div className="text-yellow-400 flex">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-half-fill"></i></div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-1">{t.products.items[0].name}</h3>
                <p className="text-gray-600 text-sm mb-3">
                  {t.products.items[0].desc}
                </p>
                <div className="flex justify-center">
                  <button className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium inline-flex items-center justify-center cursor-pointer hover:bg-primary/90">
                    {t.products.cta}
                  </button>
                </div>
              </div>
            </div>

            {/* Product 2 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Microsoft%20365%20Business%20Premium%20software%20package%20with%20office%20productivity%20icons%20on%20clean%20white%20background&width=400&height=400&seq=ms365prod&orientation=squarish"
                  alt={t.products.items[1].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">{t.products.categories.software}</span>
                  </div>
                  <div className="text-yellow-400 flex">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-1">{t.products.items[1].name}</h3>
                <p className="text-gray-600 text-sm mb-3">
                  {t.products.items[1].desc}
                </p>
                <div className="flex justify-center">
                  <button className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium inline-flex items-center justify-center cursor-pointer hover:bg-primary/90">
                    {t.products.cta}
                  </button>
                </div>
              </div>
            </div>

            {/* Product 3 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Cisco%20Firepower%201120%20NGFW%20next-generation%20firewall%20on%20clean%20white%20background%2C%20enterprise%20security%20appliance&width=400&height=400&seq=ciscofirepower&orientation=squarish"
                  alt={t.products.items[2].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded-full">{t.products.categories.hardware}</span>
                  </div>
                  <div className="text-yellow-400 flex">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-line"></i></div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-1">{t.products.items[2].name}</h3>
                <p className="text-gray-600 text-sm mb-3">
                  {t.products.items[2].desc}
                </p>
                <div className="flex justify-center">
                  <button className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium inline-flex items-center justify-center cursor-pointer hover:bg-primary/90">
                    {t.products.cta}
                  </button>
                </div>
              </div>
            </div>

            {/* Product 4 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Enterprise%20AI%20implementation%20services%20with%20artificial%20intelligence%20and%20machine%20learning%20technology%20on%20clean%20white%20background&width=400&height=400&seq=enterpriseai&orientation=squarish"
                  alt={t.products.items[3].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">{t.products.categories.aiServices}</span>
                  </div>
                  <div className="text-yellow-400 flex">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-half-fill"></i></div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-1">{t.products.items[3].name}</h3>
                <p className="text-gray-600 text-sm mb-3">
                  {t.products.items[3].desc}
                </p>
                <div className="flex justify-center">
                  <button className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium inline-flex items-center justify-center cursor-pointer hover:bg-primary/90">
                    {t.products.cta}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive IT Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">{t.services.title}</h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {t.services.subtitle}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Cloud Services */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-cloud-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.services.cloud.title}</h3>
              <p className="text-gray-600 mb-4">
                {t.services.cloud.desc}
              </p>
              <ul className="space-y-2 mb-6">
                {t.services.cloud.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href={`${isNL ? '/nl' : ''}/cloud-services`} className="text-primary font-medium flex items-center cursor-pointer hover:underline">
                {t.common.learnMore}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Cybersecurity */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-shield-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.services.security.title}</h3>
              <p className="text-gray-600 mb-4">
                {t.services.security.desc}
              </p>
              <ul className="space-y-2 mb-6">
                {t.services.security.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href={`${isNL ? '/nl' : ''}/cyber-security`} className="text-green-600 font-medium flex items-center cursor-pointer hover:underline">
                {t.common.learnMore}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* AI Integration */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-14 h-14 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-ai-generate ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.services.ai.title}</h3>
              <p className="text-gray-600 mb-4">
                {t.services.ai.desc}
              </p>
              <ul className="space-y-2 mb-6">
                {t.services.ai.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href={`${isNL ? '/nl' : ''}/ai-consulting`} className="text-purple-600 font-medium flex items-center cursor-pointer hover:underline">
                {t.common.learnMore}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Transform Your Business with Enterprise AI */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">{t.aiSection.label}</span>
              <h2 className="text-3xl font-bold mb-6">{t.aiSection.title}</h2>
              <p className="text-gray-600 mb-6">
                {t.aiSection.desc}
              </p>

              <div className="space-y-4 mb-8">
                {t.aiSection.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <div className="w-6 h-6 flex items-center justify-center text-primary">
                        <i className={idx === 0 ? 'ri-robot-line' : idx === 1 ? 'ri-database-2-line' : 'ri-code-box-line'}></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a 
                href={`${isNL ? '/nl' : ''}/ai-consulting`}
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                {t.aiSection.cta}
              </a>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Enterprise%20AI%20and%20artificial%20intelligence%20technology%20with%20neural%20network%20visualization%20and%20digital%20brain%20concept%20in%20blue%20tones%2C%20futuristic%20AI%20technology%20background&width=800&height=600&seq=aitech&orientation=landscape"
                  alt="Enterprise AI"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scalable IT Infrastructure */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20data%20center%20with%20server%20racks%20and%20blue%20LED%20lights%2C%20enterprise%20IT%20infrastructure%20with%20networking%20equipment%20in%20professional%20facility&width=800&height=600&seq=datacenter&orientation=landscape"
                  alt="Data Center Infrastructure"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">{t.infrastructure.label}</span>
              <h2 className="text-3xl font-bold mb-6">{t.infrastructure.title}</h2>
              <p className="text-gray-600 mb-6">
                {t.infrastructure.desc}
              </p>

              <div className="space-y-4">
                {t.infrastructure.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <div className="w-6 h-6 flex items-center justify-center text-green-600">
                        <i className={idx === 0 ? 'ri-server-line' : idx === 1 ? 'ri-cloud-line' : 'ri-shield-check-line'}></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modernize Your Business Operations */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">{t.digital.label}</span>
              <h2 className="text-3xl font-bold mb-6">{t.digital.title}</h2>
              <p className="text-gray-600 mb-6">
                {t.digital.desc}
              </p>

              <div className="space-y-4 mb-8">
                {t.digital.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                        <i className={idx === 0 ? 'ri-smartphone-line' : idx === 1 ? 'ri-line-chart-line' : 'ri-settings-3-line'}></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button className="bg-purple-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-purple-700 transition-colors cursor-pointer">
                {t.digital.cta}
              </button>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Digital%20transformation%20and%20business%20modernization%20with%20professional%20team%20collaborating%20in%20modern%20office%20with%20technology%20and%20digital%20screens&width=800&height=600&seq=digitaltrans&orientation=landscape"
                  alt="Digital Transformation"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/85fde6dc8dac8c3eb5268337d87d2976.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              {t.cta.title}
            </h2>
            <p className="text-white/90 mb-8">
              {t.cta.desc}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={`${isNL ? '/nl' : ''}/contact`}
                className="bg-white text-primary px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
              >
                {t.cta.primary}
              </a>
              <a 
                href="#" 
                className="bg-transparent border border-white text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer"
              >
                {t.cta.secondary}
              </a>
            </div>
          </div>
        </div>
      </section>

      <PartnerForm />
      <Footer />
      <ChatWidget />
    </div>
  );
};

export default HomePage;
